import * as THREE from 'three';
import { BlockId } from '../world/BlockType.js';

/**
 * BoatSystem - Simple boat mechanics.
 * Right-click on water with BOAT item to place a boat entity.
 * Right-click boat to mount. WASD to navigate. Space to dismount.
 * Speed: 6.0 (faster than swimming 3.0).
 */

const BOAT_SPEED = 6.0;
const BOAT_WIDTH = 1.5;
const BOAT_HEIGHT = 0.5;
const BOAT_DEPTH = 0.8;
const BOAT_COLOR = 0x8B6B3E;
const INTERACT_DIST = 4;

export class BoatSystem {
  constructor() {
    this.inBoat = false;
    this.boatPos = null;
    this.boatMesh = null;
    this._speed = BOAT_SPEED;
  }

  /**
   * Place and enter a boat at the given position.
   */
  enterBoat(x, y, z, scene) {
    if (this.boatMesh) this.exitBoat(scene);

    const geo = new THREE.BoxGeometry(BOAT_WIDTH, BOAT_HEIGHT, BOAT_DEPTH);
    const mat = new THREE.MeshLambertMaterial({ color: BOAT_COLOR });
    this.boatMesh = new THREE.Mesh(geo, mat);
    this.boatMesh.position.set(x, y, z);
    scene.add(this.boatMesh);

    this.boatPos = { x, y, z };
    this.inBoat = true;
    if (this.onPlayVideo) this.onPlayVideo('/boat.webm', 2);
  }

  /**
   * Dismount and remove the boat mesh.
   */
  exitBoat(scene) {
    if (this.boatMesh) {
      scene.remove(this.boatMesh);
      this.boatMesh.geometry.dispose();
      this.boatMesh.material.dispose();
      this.boatMesh = null;
    }
    this.inBoat = false;
    this.boatPos = null;
  }

  /**
   * Update boat movement each frame.
   * WASD moves the boat on the water surface. Space dismounts.
   * @param {object} camera - FPSCamera instance (for getForward/getRight)
   */
  update(dt, inputManager, player, world, scene, camera) {
    if (!this.inBoat || !this.boatPos) return;

    // Space = exit boat
    if (inputManager.isKeyDown('Space')) {
      const exitPos = { x: this.boatPos.x, y: this.boatPos.y + 1, z: this.boatPos.z };
      this.exitBoat(scene);
      player.position.set(exitPos.x, exitPos.y, exitPos.z);
      player.velocity.set(0, 0, 0);
      return;
    }

    // Movement direction from WASD using camera orientation
    const forward = camera.getForward();
    const right = camera.getRight();
    let mx = 0, mz = 0;

    if (inputManager.isKeyDown('KeyW')) { mx += forward.x; mz += forward.z; }
    if (inputManager.isKeyDown('KeyS')) { mx -= forward.x; mz -= forward.z; }
    if (inputManager.isKeyDown('KeyD')) { mx += right.x; mz += right.z; }
    if (inputManager.isKeyDown('KeyA')) { mx -= right.x; mz -= right.z; }

    if (mx !== 0 || mz !== 0) {
      const len = Math.sqrt(mx * mx + mz * mz);
      mx /= len;
      mz /= len;
      this.boatPos.x += mx * this._speed * dt;
      this.boatPos.z += mz * this._speed * dt;
    }

    // Keep boat at water surface level
    const bx = Math.floor(this.boatPos.x);
    const bz = Math.floor(this.boatPos.z);
    let waterY = Math.floor(this.boatPos.y);

    // Find water surface: scan down from current Y
    for (let y = waterY + 2; y >= waterY - 2; y--) {
      if (world.getBlock(bx, y, bz) === BlockId.WATER &&
          world.getBlock(bx, y + 1, bz) !== BlockId.WATER) {
        waterY = y + 1;
        break;
      }
    }
    this.boatPos.y = waterY;

    // Update mesh position
    if (this.boatMesh) {
      this.boatMesh.position.set(this.boatPos.x, this.boatPos.y, this.boatPos.z);
    }

    // Player rides the boat
    player.position.set(this.boatPos.x, this.boatPos.y + BOAT_HEIGHT, this.boatPos.z);
    player.velocity.set(0, 0, 0);
  }
}
